'use client';

import { getLetterGrade, getGradeColor, getScoreColor, calculateRingPath, formatScore } from '@/lib/quality/coaching-utils';
import { cn } from '@/lib/cn';

interface ScoreRingProps {
  score: number;
  size?: number;
  strokeWidth?: number;
  showLabel?: boolean;
  className?: string;
}

export function ScoreRing({ score, size = 44, strokeWidth = 4, showLabel = true, className }: ScoreRingProps) {
  const safeScore = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0;
  const grade = getLetterGrade(safeScore);
  const color = getGradeColor(grade);
  const scoreColor = getScoreColor(safeScore);
  const { circumference, dashOffset, cx, cy, r } = calculateRingPath(safeScore, size / 2, strokeWidth);

  // Map grade colors to SVG stroke colors using oklch for dark-mode compatibility
  const strokeColors: Record<string, string> = {
    'text-emerald-600': 'oklch(0.75 0.12 180)',  // teal
    'text-blue-600': 'oklch(0.65 0.15 280)',      // purple
    'text-amber-500': 'oklch(0.80 0.14 90)',      // yellow-green
    'text-orange-500': 'oklch(0.70 0.16 45)',     // amber
    'text-red-600': 'oklch(0.65 0.18 15)',        // warm red
  };

  const strokeColor = strokeColors[color] ?? 'oklch(0.55 0 0)';

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      {showLabel && (
        <span className={cn('absolute text-xs font-bold border rounded-sm px-0.5', scoreColor)}>
          {formatScore(safeScore)}
        </span>
      )}
    </div>
  );
}
